/**
 * The purpose of this class is to easily retrieve key/val pairs from the url hash or search params.
 * It also allows us to manipulate the hash of the current url.
 * We only have ways to manipulate the hash and not the search params, since manipulating search params causes a reload of the page.
 * We only use query params when we want to trigger some sort of reload.
 * A good example is the "create" search param used in the create company app in tsbtob.
 */

class Url {
    static get hash() {
        return Object.fromEntries(
            location.hash
                .substring(1)
                .split('&')
                .filter((_) => _)
                .map((keyval) => {
                    const [key, val] = keyval.split('=')
                    return [key, decodeURIComponent(val)]
                })
        )
    }

    static get query() {
        return Object.fromEntries(
            location.search
                .substring(1)
                .split('&')
                .filter((_) => _)
                .map((keyval) => {
                    const [key, val] = keyval.split('=')
                    return [key, decodeURIComponent(val)]
                })
        )
    }

    static serialize(obj) {
        return Object.entries(obj)
            .map(([key, val]) => `${key}=${val}`)
            .join('&')
    }

    /**
     * @description Adds new key/val pair to the hash. Everything in the hash is a string, so anything you add will be converted to a string.
     * @param {String} key
     * @param {*} val
     */
    static addHash(key, val) {
        const newHash = { ...Url.hash, [key]: val }
        location.hash = Url.serialize(newHash)
    }

    static removeHash(key) {
        const newHash = { ...Url.hash }
        delete newHash[key]
        location.hash = Url.serialize(newHash)
    }

    static addHashObj(obj) {
        const newHash = { ...Url.hash }

        for (const key in obj) {
            newHash[key] = obj[key]
        }

        location.hash = Url.serialize(newHash)
    }

    static removeHashObj(obj) {
        const newHash = { ...Url.hash }

        for (const key in obj) {
            if (key in newHash) {
                delete newHash[key]
            }
        }

        location.hash = Url.serialize(newHash)
    }

    static replaceHash(obj) {
        const newHash = []

        for (const key in obj) {
            newHash.push(`${key}=${obj[key]}`)
        }

        location.hash = newHash.join('&')
    }
}

export default Url
